// SessionManager.ts - Handles browser session storage for user activity tracking

export interface SessionData {
  recentFiles: RecentFile[];
  userPreferences: UserPreferences;
  sessionStartTime: number;
  lastActivityTime: number;
}

export interface RecentFile {
  id: number;
  fileName: string;
  uploadTime: number;
  lastAccessed: number;
  isUserUploaded: boolean;
  ownerId?: number | null;
}

export interface UserPreferences {
  defaultZoomLevel: number;
  autoSaveSnippets: boolean;
  showKeyboardShortcuts: boolean;
  theme: 'light' | 'dark' | 'auto';
}

export class SessionManager {
  private static readonly STORAGE_KEY = 'auditio_session_data';
  private static readonly MAX_RECENT_FILES = 20;
  private static readonly SESSION_TIMEOUT = 7 * 24 * 60 * 60 * 1000; // 7 days
  // You can change this to any duration you want:
  // 1 hour: 1 * 60 * 60 * 1000
  // 24 hours: 24 * 60 * 60 * 1000
  // 7 days: 7 * 24 * 60 * 60 * 1000
  // 30 days: 30 * 24 * 60 * 60 * 1000
  // Never expire: Infinity

  /**
   * Initialize session data
   */
  static initializeSession(): SessionData {
    console.log('🔍 initializeSession called');
    const existingData = this.getSessionData();
    
    if (existingData && this.isSessionValid(existingData)) {
      console.log('🔍 Using existing session data');
      // Migrate existing data to include isUserUploaded property and ownerId
      existingData.recentFiles = existingData.recentFiles.map(file => ({
        ...file,
        isUserUploaded: file.isUserUploaded !== undefined ? file.isUserUploaded : false,
        ownerId: file.ownerId !== undefined ? file.ownerId : undefined
      }));
      
      // Clean up any duplicates
      this.cleanupSession();
      
      // Update last activity time
      existingData.lastActivityTime = Date.now();
      this.saveSessionData(existingData);
      return existingData;
    }

    // Create new session
    const newSession: SessionData = {
      recentFiles: [],
      userPreferences: {
        defaultZoomLevel: 1,
        autoSaveSnippets: true,
        showKeyboardShortcuts: true,
        theme: 'auto'
      },
      sessionStartTime: Date.now(),
      lastActivityTime: Date.now()
    };

    this.saveSessionData(newSession);
    return newSession;
  }

  /**
   * Add a recently uploaded file to the session
   */
  static addRecentFile(fileId: number, fileName: string, isUserUploaded: boolean = true, uploadTime?: number, ownerId?: number | null): void {
    const sessionData = this.getSessionData() || this.initializeSession();

    // Save debug info about what we're about to do
    const debugInfo = {
      timestamp: Date.now(),
      action: 'addRecentFile',
      fileId,
      fileName,
      isUserUploaded,
      existingFilesCount: sessionData.recentFiles.length,
      existingFileIds: sessionData.recentFiles.map(f => ({ id: f.id, isUserUploaded: f.isUserUploaded }))
    };
    localStorage.setItem('auditio_debug_log', JSON.stringify(debugInfo));

    // Use provided upload time or current time as fallback
    const actualUploadTime = uploadTime || Date.now();
    
    let file = sessionData.recentFiles.find(f => f.id === fileId);
    if (file) {
      // Update in place - only update uploadTime if we have a new value
      file.fileName = fileName;
      file.lastAccessed = Date.now();
      if (uploadTime) {
        file.uploadTime = actualUploadTime;
      }
      file.isUserUploaded = file.isUserUploaded || isUserUploaded;
      // Update ownerId if provided
      if (ownerId !== undefined) {
        file.ownerId = ownerId;
      }
      console.log('🔍 Updated existing file in session:', file);
    } else {
      // Add new file
      file = {
        id: fileId,
        fileName,
        uploadTime: actualUploadTime,
        lastAccessed: Date.now(),
        isUserUploaded,
        ownerId: ownerId !== undefined ? ownerId : undefined
      };
      sessionData.recentFiles.unshift(file);
      console.log('🔍 Added new file to session:', file);
    }

    // Keep only the most recent files
    if (sessionData.recentFiles.length > this.MAX_RECENT_FILES) {
      sessionData.recentFiles = sessionData.recentFiles.slice(0, this.MAX_RECENT_FILES);
    }

    sessionData.lastActivityTime = Date.now();
    this.saveSessionData(sessionData);
    this.cleanupSession();
  }

  /**
   * Update file access time when user opens a file
   */
  static updateFileAccess(fileId: number, fileName?: string, ownerId?: number | null): void {
    // Ensure session is initialized
    const sessionData = this.getSessionData() || this.initializeSession();

    // Save debug info about what we're about to do
    const debugInfo = {
      timestamp: Date.now(),
      action: 'updateFileAccess',
      fileId,
      fileName,
      existingFilesCount: sessionData.recentFiles.length,
      existingFileIds: sessionData.recentFiles.map(f => ({ id: f.id, isUserUploaded: f.isUserUploaded })),
      foundFile: !!sessionData.recentFiles.find(f => f.id === fileId)
    };
    localStorage.setItem('auditio_debug_log', JSON.stringify(debugInfo));

    console.log('🔍 updateFileAccess called:', { fileId, fileName });
    console.log('🔍 Current files in session:', sessionData.recentFiles.map(f => ({ id: f.id, fileName: f.fileName, isUserUploaded: f.isUserUploaded })));
    console.log('🔍 Looking for file ID:', fileId);
    console.log('🔍 Available file IDs:', sessionData.recentFiles.map(f => f.id));

    const file = sessionData.recentFiles.find(f => f.id === fileId);
    if (file) {
      // File already exists in recent list - just update access time
      // Preserve the existing isUserUploaded flag (never downgrade from uploaded to accessed)
      console.log('🔍 Found existing file, updating access time:', file);
      file.lastAccessed = Date.now();
      // Keep the existing isUserUploaded flag - don't change it
      // Update ownerId if provided
      if (ownerId !== undefined) {
        file.ownerId = ownerId;
      }
      sessionData.lastActivityTime = Date.now();
      this.saveSessionData(sessionData);
    } else if (fileName) {
      // File not in recent list - add it (not user uploaded)
      console.log('🔍 File not found, adding as non-user-uploaded');
      console.log('🔍 This might be a timing issue - file was just uploaded but not found in session');
      this.addRecentFile(fileId, fileName, false, undefined, ownerId);
    }
  }



  /**
   * Get recent files sorted by last accessed time
   */
  static getRecentFiles(): RecentFile[] {
    const sessionData = this.getSessionData();
    if (!sessionData) return [];

    console.log('🔍 Raw session files before deduplication:', sessionData.recentFiles.map(f => ({ id: f.id, fileName: f.fileName, isUserUploaded: f.isUserUploaded, lastAccessed: f.lastAccessed })));

    // Check if there are duplicates in the raw data
    const fileIds = sessionData.recentFiles.map(f => f.id);
    const uniqueIds = new Set(fileIds);
    if (fileIds.length !== uniqueIds.size) {
      console.error('🚨 DUPLICATES FOUND IN RAW SESSION DATA:', {
        totalFiles: fileIds.length,
        uniqueFiles: uniqueIds.size,
        duplicates: fileIds.filter((id, index) => fileIds.indexOf(id) !== index)
      });
      
      // Aggressively clean up the session data
      this.cleanupSession();
      
      // Re-read the cleaned session data
      const cleanedSessionData = this.getSessionData();
      if (cleanedSessionData) {
        console.log('🔍 After cleanup, session has:', cleanedSessionData.recentFiles.length, 'files');
        return cleanedSessionData.recentFiles.sort((a, b) => b.lastAccessed - a.lastAccessed);
      }
    }

    // Remove duplicates by file ID, keeping the most recently accessed version
    // If lastAccessed times are the same, prioritize user-uploaded files
    const uniqueFiles = new Map<number, RecentFile>();
    sessionData.recentFiles.forEach(file => {
      // Ensure file ID is a number
      const fileId = Number(file.id);
      const existing = uniqueFiles.get(fileId);
      // console.log('🔍 Processing file:', { id: fileId, originalId: file.id, isUserUploaded: file.isUserUploaded, lastAccessed: file.lastAccessed });
      if (!existing) {
        console.log('🔍 No existing file, adding:', fileId);
        uniqueFiles.set(fileId, { ...file, id: fileId });
      } else {
        console.log('🔍 Found existing file:', { id: existing.id, isUserUploaded: existing.isUserUploaded, lastAccessed: existing.lastAccessed });
        
        // Always prefer user-uploaded files over accessed files, regardless of access time
        if (file.isUserUploaded && !existing.isUserUploaded) {
          console.log('🔍 New file is user-uploaded, existing is not - replacing');
          uniqueFiles.set(fileId, { ...file, id: fileId });
        } else if (!file.isUserUploaded && existing.isUserUploaded) {
          console.log('🔍 Existing file is user-uploaded, new is not - keeping existing');
        } else if (file.lastAccessed > existing.lastAccessed) {
          console.log('🔍 Same upload status, but new file has later access time, replacing');
          uniqueFiles.set(fileId, { ...file, id: fileId });
        } else {
          console.log('🔍 Keeping existing file');
        }
      }
    });

    const deduplicatedFiles = Array.from(uniqueFiles.values());
    console.log('🔍 Deduplicated files:', deduplicatedFiles.map(f => ({ id: f.id, fileName: f.fileName, isUserUploaded: f.isUserUploaded })));

    return deduplicatedFiles.sort((a, b) => b.lastAccessed - a.lastAccessed);
  }

  /**
   * Update user preferences
   */
  static updateUserPreferences(preferences: Partial<UserPreferences>): void {
    const sessionData = this.getSessionData();
    if (!sessionData) return;

    sessionData.userPreferences = {
      ...sessionData.userPreferences,
      ...preferences
    };
    sessionData.lastActivityTime = Date.now();
    this.saveSessionData(sessionData);
  }

  /**
   * Get user preferences
   */
  static getUserPreferences(): UserPreferences | null {
    const sessionData = this.getSessionData();
    return sessionData?.userPreferences || null;
  }

  /**
   * Remove a specific file from recent files
   */
  static removeRecentFile(fileId: number): void {
    const sessionData = this.getSessionData();
    if (!sessionData) return;

    console.log('🗑️ Removing file from session:', fileId);
    console.log('🗑️ Before removal, files:', sessionData.recentFiles.map(f => ({ id: f.id, fileName: f.fileName, idType: typeof f.id })));
    console.log('🗑️ File ID to remove:', fileId, 'type:', typeof fileId);

    const initialCount = sessionData.recentFiles.length;
    // Convert both IDs to numbers for comparison to handle type mismatches
    sessionData.recentFiles = sessionData.recentFiles.filter(file => Number(file.id) !== Number(fileId));
    const finalCount = sessionData.recentFiles.length;

    console.log('🗑️ After removal, files:', sessionData.recentFiles.map(f => ({ id: f.id, fileName: f.fileName })));
    console.log('🗑️ Removed files:', initialCount - finalCount);

    sessionData.lastActivityTime = Date.now();
    this.saveSessionData(sessionData);
  }

  /**
   * Clear session data
   */
  static clearSession(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Clean up session data by removing duplicates
   */
  static cleanupSession(): void {
    const sessionData = this.getSessionData();
    if (!sessionData) return;

    console.log('🧹 Cleaning up session, original files:', sessionData.recentFiles.map(f => ({ id: f.id, fileName: f.fileName, isUserUploaded: f.isUserUploaded })));

    // Remove duplicates by file ID, keeping the most recently accessed version
    // If lastAccessed times are the same, prioritize user-uploaded files
    const uniqueFiles = new Map<number, RecentFile>();
    sessionData.recentFiles.forEach(file => {
      // Ensure file ID is a number
      const fileId = Number(file.id);
      const existing = uniqueFiles.get(fileId);
      if (!existing) {
        uniqueFiles.set(fileId, { ...file, id: fileId });
      } else if (file.isUserUploaded && !existing.isUserUploaded) {
        // Always prefer user-uploaded files over accessed files
        uniqueFiles.set(fileId, { ...file, id: fileId });
      } else if (!file.isUserUploaded && existing.isUserUploaded) {
        // Keep existing user-uploaded file
      } else if (file.lastAccessed > existing.lastAccessed) {
        uniqueFiles.set(fileId, { ...file, id: fileId });
      }
    });

    const beforeCount = sessionData.recentFiles.length;
    sessionData.recentFiles = Array.from(uniqueFiles.values());
    const afterCount = sessionData.recentFiles.length;
    
    this.saveSessionData(sessionData);
    
    console.log('🧹 Cleaned up session, removed duplicates:', { beforeCount, afterCount, removed: beforeCount - afterCount });
    console.log('🧹 Final files:', sessionData.recentFiles.map(f => ({ id: f.id, fileName: f.fileName, isUserUploaded: f.isUserUploaded })));
  }

  /**
   * Get session statistics
   */
  static getSessionStats(): {
    totalFiles: number;
    sessionDuration: number;
    lastActivity: number;
  } {
    const sessionData = this.getSessionData();
    if (!sessionData) {
      return {
        totalFiles: 0,
        sessionDuration: 0,
        lastActivity: 0
      };
    }

    const sessionDuration = Date.now() - sessionData.sessionStartTime;

    return {
      totalFiles: sessionData.recentFiles.length,
      sessionDuration,
      lastActivity: sessionData.lastActivityTime
    };
  }

  /**
   * Get debug log from localStorage
   */
  static getDebugLog(): any {
    try {
      const debugData = localStorage.getItem('auditio_debug_log');
      return debugData ? JSON.parse(debugData) : null;
    } catch (error) {
      console.error('Error reading debug log:', error);
      return null;
    }
  }

  /**
   * Check if session is still valid (not expired)
   */
  private static isSessionValid(sessionData: SessionData): boolean {
    const timeSinceLastActivity = Date.now() - sessionData.lastActivityTime;
    return timeSinceLastActivity < this.SESSION_TIMEOUT;
  }

  /**
   * Get session data from localStorage
   */
  private static getSessionData(): SessionData | null {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      console.log('🔍 Reading from localStorage:', { key: this.STORAGE_KEY, data: data ? 'exists' : 'null' });
      if (data) {
        const parsed = JSON.parse(data);
        console.log('🔍 Parsed session data:', { 
          recentFilesCount: parsed.recentFiles?.length || 0,
          fileIds: parsed.recentFiles?.map((f: any) => f.id) || []
        });
        return parsed;
      }
      return null;
    } catch (error) {
      console.error('Error reading session data:', error);
      return null;
    }
  }

  /**
   * Save session data to localStorage
   */
  private static saveSessionData(sessionData: SessionData): void {
    try {
      // Check for duplicates before saving
      const fileIds = sessionData.recentFiles.map(f => f.id);
      const uniqueIds = new Set(fileIds);
      if (fileIds.length !== uniqueIds.size) {
        console.error('🚨 DUPLICATE DETECTED BEFORE SAVE:', {
          totalFiles: fileIds.length,
          uniqueFiles: uniqueIds.size,
          duplicates: fileIds.filter((id, index) => fileIds.indexOf(id) !== index),
          allFiles: sessionData.recentFiles.map(f => ({ id: f.id, fileName: f.fileName, isUserUploaded: f.isUserUploaded }))
        });
      }
      
      const dataToSave = JSON.stringify(sessionData);
      localStorage.setItem(this.STORAGE_KEY, dataToSave);
      
      // Also save debug info that persists across navigation
      const debugInfo = {
        timestamp: Date.now(),
        action: 'saveSessionData',
        fileIds: sessionData.recentFiles.map(f => ({ id: f.id, fileName: f.fileName, isUserUploaded: f.isUserUploaded })),
        totalFiles: sessionData.recentFiles.length,
        hasDuplicates: fileIds.length !== uniqueIds.size
      };
      localStorage.setItem('auditio_debug_log', JSON.stringify(debugInfo));
      
      console.log('🔍 Saved to localStorage:', { 
        key: this.STORAGE_KEY, 
        dataLength: dataToSave.length,
        fileIds: sessionData.recentFiles.map(f => f.id),
        hasDuplicates: fileIds.length !== uniqueIds.size
      });
    } catch (error) {
      console.error('Error saving session data:', error);
    }
  }
} 